import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './SellerSidebar.css';

const API_BASE_URL = '/api';

const SellerSidebar = ({ activeItem }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sellerLogo, setSellerLogo] = useState(null);

  // Fetch seller logo if not available or truncated
  useEffect(() => {
    const fetchSellerLogo = async () => {
      if (user?.sellerId || user?.sellerProfileId) {
        const sellerId = user.sellerId || user.sellerProfileId;
        try {
          const response = await fetch(`${API_BASE_URL}/sellers/${sellerId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.logo) {
              setSellerLogo(data.logo);
            }
          }
        } catch (error) {
          console.error('Error fetching seller logo:', error);
        }
      }
    };

    // Check if logo needs to be fetched (truncated or missing)
    const currentLogo = user?.companyLogo;
    if (!currentLogo || currentLogo.includes('...truncated')) {
      fetchSellerLogo();
    } else {
      setSellerLogo(currentLogo);
    }
  }, [user]);

  // Sidebar menu items
  const sidebarMenuItems = [
    { id: 'dashboard', icon: 'home', label: 'Dashboard', path: '/seller-dashboard', hasSubmenu: false },
    { id: 'products', icon: 'list', label: 'Product Listing', path: '/product-manage', hasSubmenu: true },
    { id: 'rfq', icon: 'bell', label: 'RFQ Manage', path: '/rfq-manage', hasSubmenu: true },
    { id: 'inquiries', icon: 'mail', label: 'Inquiries', path: '/supplier-inquiries', hasSubmenu: false },
    { id: 'my-inquiries', icon: 'mail', label: 'My Inquiries', path: '/my-inquiries', hasSubmenu: false },
    { id: 'messages', icon: 'chat', label: 'Messages', path: '/messages', hasSubmenu: true },
    { id: 'membership', icon: 'invoice', label: 'Membership Plan', path: '/membership-packages', hasSubmenu: true },
    { id: 'favorite', icon: 'heart', label: 'Saved Suppliers', path: '/favorites-supplier', hasSubmenu: false },
    { id: 'favorite-products', icon: 'heart', label: 'Saved Products', path: '/favorites-products', hasSubmenu: false },
    { id: 'profile', icon: 'user', label: 'Profile', path: '/profile', hasSubmenu: false },
    { id: 'logout', icon: 'logout', label: 'Logout', path: '/', hasSubmenu: false, isLogout: true },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'home':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'list':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3.25 7C3.25 6.80109 3.32902 6.61032 3.46967 6.46967C3.61032 6.32902 3.80109 6.25 4 6.25H20C20.1989 6.25 20.3897 6.32902 20.5303 6.46967C20.671 6.61032 20.75 6.80109 20.75 7C20.75 7.19891 20.671 7.38968 20.5303 7.53033C20.3897 7.67098 20.1989 7.75 20 7.75H4C3.80109 7.75 3.61032 7.67098 3.46967 7.53033C3.32902 7.38968 3.25 7.19891 3.25 7ZM3.25 12C3.25 11.8011 3.32902 11.6103 3.46967 11.4697C3.61032 11.329 3.80109 11.25 4 11.25H15C15.1989 11.25 15.3897 11.329 15.5303 11.4697C15.671 11.6103 15.75 11.8011 15.75 12C15.75 12.1989 15.671 12.3897 15.5303 12.5303C15.3897 12.671 15.1989 12.75 15 12.75H4C3.80109 12.75 3.61032 12.671 3.46967 12.5303C3.32902 12.3897 3.25 12.1989 3.25 12ZM3.25 17C3.25 16.8011 3.32902 16.6103 3.46967 16.4697C3.61032 16.329 3.80109 16.25 4 16.25H9C9.19891 16.25 9.38968 16.329 9.53033 16.4697C9.67098 16.6103 9.75 16.8011 9.75 17C9.75 17.1989 9.67098 17.3897 9.53033 17.5303C9.38968 17.671 9.19891 17.75 9 17.75H4C3.80109 17.75 3.61032 17.671 3.46967 17.5303C3.32902 17.3897 3.25 17.1989 3.25 17Z" fill="#505050"/>
          </svg>
        );
      case 'bell':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'chat':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.25 7.5H17.25V4.5C17.25 4.10218 17.092 3.72064 16.8107 3.43934C16.5294 3.15804 16.1478 3 15.75 3H3.75C3.35218 3 2.97064 3.15804 2.68934 3.43934C2.40804 3.72064 2.25 4.10218 2.25 4.5V15C2.25 15.1989 2.32902 15.3897 2.46967 15.5303C2.61032 15.671 2.80109 15.75 3 15.75C3.17068 15.7507 3.33597 15.6905 3.46875 15.5831L6.97594 12.75H8.25V15C8.25 15.3978 8.40804 15.7794 8.68934 16.0607C8.97064 16.342 9.35218 16.5 9.75 16.5H17.0241L20.5312 19.3331C20.664 19.4405 20.8293 19.4993 21 19.5C21.1989 19.5 21.3897 19.421 21.5303 19.2803C21.671 19.1397 21.75 18.9489 21.75 18.75V9C21.75 8.60218 21.592 8.22064 21.3107 7.93934C21.0294 7.65804 20.6478 7.5 20.25 7.5ZM6.23906 11.1669L3.75 13.1797V4.5H15.75V12.75H6.71063C6.53897 12.75 6.37252 12.8089 6.23906 12.9169V11.1669ZM20.25 17.6797L17.7609 15.6669C17.6282 15.5595 17.4629 15.5007 17.2922 15.5H8.25V14.25H15.75C16.1478 14.25 16.5294 14.092 16.8107 13.8107C17.092 13.5294 17.25 13.1478 17.25 12.75V9H20.25V17.6797Z" fill="#333333"/>
          </svg>
        );
      case 'mail':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 6L12 13L2 6" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'invoice':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.625 18.5625H3C2.5526 18.562 2.12367 18.3841 1.80731 18.0677C1.49095 17.7513 1.313 17.3224 1.3125 16.875V4.875C1.3125 3.94463 2.06963 3.1875 3 3.1875H5.625C5.77418 3.1875 5.91726 3.24676 6.02275 3.35225C6.12824 3.45774 6.1875 3.60082 6.1875 3.75C6.1875 3.89918 6.12824 4.04226 6.02275 4.14775C5.91726 4.25324 5.77418 4.3125 5.625 4.3125H3C2.68988 4.3125 2.4375 4.56488 2.4375 4.875V16.875C2.4375 17.1851 2.68988 17.4375 3 17.4375H5.625C5.77418 17.4375 5.91726 17.4968 6.02275 17.6023C6.12824 17.7077 6.1875 17.8508 6.1875 18C6.1875 18.1492 6.12824 18.2923 6.02275 18.3978C5.91726 18.5032 5.77418 18.5625 5.625 18.5625Z" fill="#333333"/>
            <path d="M17.625 22.6875C14.8335 22.6875 12.5625 20.4165 12.5625 17.625C12.5625 14.8335 14.8335 12.5625 17.625 12.5625C20.4165 12.5625 22.6875 14.8335 22.6875 17.625C22.6875 20.4165 20.4165 22.6875 17.625 22.6875ZM17.625 13.6875C15.4538 13.6875 13.6875 15.4538 13.6875 17.625C13.6875 19.7963 15.4538 21.5625 17.625 21.5625C19.7963 21.5625 21.5625 19.7963 21.5625 17.625C21.5625 15.4538 19.7963 13.6875 17.625 13.6875Z" fill="#333333"/>
            <path d="M16.9681 19.687C16.819 19.687 16.676 19.6278 16.5706 19.5224L14.8831 17.8349C14.7777 17.7295 14.7185 17.5865 14.7185 17.4374C14.7185 17.2882 14.7777 17.1452 14.8831 17.0399C14.9885 16.9345 15.1315 16.8752 15.2806 16.8752C15.4298 16.8752 15.5728 16.9345 15.6781 17.0399L16.9681 18.3299L19.5731 15.7249C19.6785 15.6195 19.8215 15.5602 19.9706 15.5602C20.1198 15.5602 20.2628 15.6195 20.3681 15.7249C20.4735 15.8303 20.5328 15.9733 20.5328 16.1224C20.5328 16.2716 20.4735 16.4146 20.3681 16.5199L17.3656 19.5224C17.2602 19.6278 17.1172 19.687 16.9681 19.687Z" fill="#333333"/>
            <path d="M11.25 20.4375H6.75C6.3026 20.437 5.87367 20.2591 5.55731 19.9427C5.24095 19.6263 5.063 19.1974 5.0625 18.75V3C5.0625 2.06963 5.81963 1.3125 6.75 1.3125H15C15.9304 1.3125 16.6875 2.06963 16.6875 3V11.25C16.6875 11.3992 16.6282 11.5423 16.5227 11.6477C16.4173 11.7532 16.2742 11.8125 16.125 11.8125C15.9758 11.8125 15.8327 11.7532 15.7273 11.6477C15.6218 11.5423 15.5625 11.3992 15.5625 11.25V3C15.5625 2.68988 15.3101 2.4375 15 2.4375H6.75C6.43988 2.4375 6.1875 2.68988 6.1875 3V18.75C6.1875 19.0601 6.43988 19.3125 6.75 19.3125H11.25C11.3992 19.3125 11.5423 19.3718 11.6477 19.4773C11.7532 19.5827 11.8125 19.7258 11.8125 19.875C11.8125 20.0242 11.7532 20.1673 11.6477 20.2727C11.5423 20.3782 11.3992 20.4375 11.25 20.4375Z" fill="#333333"/>
          </svg>
        );
      case 'heart':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.8401 4.60987C20.3294 4.09888 19.7229 3.69352 19.0555 3.41696C18.388 3.14039 17.6726 2.99805 16.9501 2.99805C16.2276 2.99805 15.5122 3.14039 14.8448 3.41696C14.1773 3.69352 13.5709 4.09888 13.0601 4.60987L12.0001 5.66987L10.9401 4.60987C9.90843 3.57818 8.50915 2.99858 7.05012 2.99858C5.59109 2.99858 4.19181 3.57818 3.16012 4.60987C2.12843 5.64156 1.54883 7.04084 1.54883 8.49987C1.54883 9.9589 2.12843 11.3582 3.16012 12.3899L4.22012 13.4499L12.0001 21.2299L19.7801 13.4499L20.8401 12.3899C21.3511 11.8791 21.7565 11.2727 22.033 10.6052C22.3096 9.93777 22.4519 9.22236 22.4519 8.49987C22.4519 7.77738 22.3096 7.06198 22.033 6.39452C21.7565 5.72706 21.3511 5.12063 20.8401 4.60987V4.60987Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'follow':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z" fill="#333333" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'user':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'logout':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#FF7474" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 17L21 12L16 7" stroke="#FF7474" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 12H9" stroke="#FF7474" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const isActivePath = (itemPath, itemId) => {
    // First check if activeItem prop is passed
    if (activeItem && activeItem === itemId) {
      return true;
    }
    if (itemId === 'dashboard' && location.pathname === '/seller-dashboard') {
      return true;
    }
    if (itemId === 'products' && (location.pathname === '/product-manage' || location.pathname === '/product-input')) {
      return true;
    }
    if (itemId === 'profile' && (location.pathname === '/profile' || location.pathname === '/my-profile' || location.pathname === '/supplier-profile')) {
      return true;
    }
    if (itemId === 'inquiries' && location.pathname === '/supplier-inquiries') {
      return true;
    }
    return location.pathname === itemPath;
  };

  // Get company name from user if available
  const companyName = user?.companyName || 'My Company';

  // Navigate to home page when logo is clicked
  const handleLogoClick = () => {
    navigate('/');
  };

  // Get profile image - priority: fetched sellerLogo > user.avatar > default
  const defaultImage = '/uploads/builder-images/f2c82df6909e2e7512c02281532c47940c81ce19_w520.png';
  const profileImage = sellerLogo || user?.avatar || defaultImage;

  return (
    <aside className="seller-sidebar">
      <div className="sidebar-profile" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
        <img 
          src={profileImage} 
          alt="Company Profile" 
          className="profile-image"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <h3 className="company-name">{companyName}</h3>
      </div>
      <nav className="sidebar-nav">
        {sidebarMenuItems.map((item) => {
          const isActive = isActivePath(item.path, item.id);
          
          if (item.isLogout) {
            return (
              <div 
                key={item.id}
                className="nav-item logout"
                onClick={handleLogout}
              >
                <div className="nav-item-content">
                  {renderIcon(item.icon)}
                  <span>{item.label}</span>
                </div>
              </div>
            );
          }
          
          return (
            <Link 
              key={item.id} 
              to={item.path}
              className={`nav-item ${isActive ? 'active' : ''}`}
            >
              <div className="nav-item-content">
                {renderIcon(item.icon)}
                <span>{item.label}</span>
              </div>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
};

export default SellerSidebar;
